'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Captcha from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '~/components/Button';
import { Checkbox } from '~/components/Checkbox';
import { Drawer } from '~/components/Drawer';
import { Input } from '~/components/Input';
import { Link } from '~/components/Link';
import { Notification } from '~/components/Notification';
import { useToasts } from '~/components/Toast';
import { useCaptcha } from '~/hooks/useCaptcha';
import { trackEmailSubmitted, trackEvent } from '~/utils';
import styles from './EmailTripDialog.module.css';
import { useEmailTrip } from './useEmailTrip';
interface EmailTripDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  tripTitle: string;
  onSuccess?: () => void;
}
interface EmailTripFormData {
  email: string;
  marketingOptOut: boolean;
}
export function EmailTripDialog({
  isOpen,
  onOpenChange,
  tripTitle,
  onSuccess
}: EmailTripDialogProps) {
  const t = useTranslations();
  const toasts = useToasts();
  const {
    submit,
    isLoading,
    isError,
    reset: resetMutation
  } = useEmailTrip();
  const {
    captchaRef,
    onCaptchaErrored,
    executeCaptcha
  } = useCaptcha();
  const [overlayEl, setOverlayEl] = useState<HTMLElement | null>(null);
  const [submitFailed, setSubmitFailed] = useState(false);
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA;
  const formSchema = useMemo(() => z.object({
    email: z.string().min(1, t('account.email_trip_email_required')).email(t('content.feedback.email_invalid_format')),
    marketingOptOut: z.boolean()
  }), [t]);
  const {
    control,
    handleSubmit,
    reset,
    formState: {
      errors
    }
  } = useForm<EmailTripFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      marketingOptOut: false
    }
  });
  useEffect(() => {
    if (!isOpen) {
      reset();
      resetMutation();
    }
  }, [isOpen, reset, resetMutation]);
  const onSubmit = async (data: EmailTripFormData) => {
    const tripLink = globalThis.window === undefined ? '' : globalThis.location.href;
    setSubmitFailed(false);
    try {
      const captcha = await executeCaptcha();
      await submit({
        email: data.email,
        marketingOptOut: data.marketingOptOut,
        tripLink,
        tripTitle,
        captcha: captcha ?? ''
      });
      trackEmailSubmitted(data.email, 'email_trip', {
        marketingOptOut: data.marketingOptOut
      });
      trackEvent('trip_page_email_me_submitted', {
        event_category: 'click',
        event_label: 'trip-page-email-me',
        newsletter_consent: !data.marketingOptOut,
        marketing_consent: !data.marketingOptOut
      });
      toasts.add({
        title: t('account.email_trip_success'),
        variant: 'success',
        position: 'middle'
      }, {
        timeout: 5000
      });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      setSubmitFailed(true);
    } finally {
      captchaRef.current?.reset();
    }
  };
  return <Drawer isOpen={isOpen} close={() => onOpenChange(false)} title={t('account.email_trip_heading')} showAsDialogInDesktop maxWidth={520} overlayRef={setOverlayEl}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.form} data-test-id="email-trip-form">
        <Controller name="email" control={control} render={({
        field: {
          value,
          onChange
        }
      }) => <Input id="email" name="email" type="email" value={value} onChange={onChange} label={t('account.email_trip_field_label')} placeholder={t('forms.email_placeholder')} errorMessage={errors.email?.message} required />} />

        {recaptchaSiteKey && overlayEl ? createPortal(<div className={styles.captchaBadge}>
                <Captcha ref={captchaRef} size="invisible" sitekey={recaptchaSiteKey} onErrored={onCaptchaErrored} />
              </div>, overlayEl) : null}

        {(isError || submitFailed) && <Notification state="error" message={t('account.email_trip_error')} />}

        <div className={styles.footer}>
          <Button variant="tertiary" size="fullWidth" onPress={() => onOpenChange(false)}>
            {t('forms.cancel')}
          </Button>
          <Button type="submit" size="fullWidth" isLoading={isLoading}>
            {t('account.email_trip_submit')}
          </Button>
        </div>

        <p className={styles.marketingCopy}>
          {t('account.email_trip_marketing_copy')}{' '}
          {t.rich('account.email_trip_privacy', {
          link: chunks => <Link href="/privacy-policy" hasUnderline>
                {chunks}
              </Link>
        })}
        </p>

        <Controller name="marketingOptOut" control={control} render={({
        field
      }) => <Checkbox id="marketingOptOut" name={field.name} checked={field.value} onChange={e => field.onChange(e.target.checked)} label={t('account.email_trip_optout')} />} />
      </form>
    </Drawer>;
}